import { add, differenceInCalendarDays, endOfDay, endOfMonth, format, startOfDay, startOfMonth } from 'date-fns';

import { DateRangeType } from '../../constants';
import { RangeType } from '../../enums';
import { IChartMeasurement, IPhysiologicalMeasurement, IPhysiologicalMeasurements } from '../../interfaces';
import { toUTCDate } from '../../utils';

const getDate = (timestamp: number, rangeType: RangeType) => {
  switch (rangeType) {
    case RangeType.DAY:
      return format(timestamp, 'h aaa');
    case RangeType.WEEK:
      return format(timestamp, 'd EEE');
    case RangeType.MONTH:
      return format(timestamp, 'd MMM');
    case RangeType.YEAR:
      return format(timestamp, 'LLL yyyy');
  }
};

const getTooltipText = (timestamp: number, rangeType: RangeType, dateFrom: Date, dateTo: Date) => {
  switch (rangeType) {
    case RangeType.DAY:
      return `${format(timestamp, 'haaa')} - ${format(add(timestamp, { hours: 2 }), 'haaa')}`;
    case RangeType.WEEK:
    case RangeType.MONTH:
      return format(timestamp, 'd MMM, yyyy');
    case RangeType.YEAR: {
      const date = new Date(timestamp);
      if (date.getFullYear() === dateFrom.getFullYear() && date.getMonth() === dateFrom.getMonth()) {
        return `${format(dateFrom, 'd')} - ${format(endOfMonth(dateFrom), 'd MMM yyyy')}`;
      } else if (date.getFullYear() === dateTo.getFullYear() && date.getMonth() === dateTo.getMonth()) {
        return `${format(startOfMonth(dateTo), 'd')} - ${format(dateTo, 'd MMM yyyy')}`;
      }
      return `${format(startOfMonth(timestamp), 'd')} - ${format(endOfMonth(timestamp), 'd MMM yyyy')}`;
    }
  }
};

const getDates = (rangeType: RangeType, dateFrom: Date, dateTo: Date) => {
  const dateRangeType = DateRangeType[rangeType];
  if (rangeType === RangeType.DAY) {
    const dayInSeconds = 24 * 60 * 60;
    const count = dayInSeconds / dateRangeType.intervalSeconds;
    const date = startOfDay(Date.now());
    return Array.from(Array(count), (_, index) => {
      const timestamp = add(date, { seconds: dateRangeType.intervalSeconds * index }).getTime();
      return { date: getDate(timestamp, rangeType), timestamp };
    });
  }

  const from = startOfDay(dateFrom);
  const to = endOfDay(dateTo);
  const getCount = () => {
    if (rangeType !== RangeType.YEAR) {
      return Math.round((to.getTime() - from.getTime()) / 1000 / dateRangeType.intervalSeconds);
    }
    return from.getFullYear() === to.getFullYear() ? 12 : 13;
  };
  const count = getCount();
  return Array.from(Array(count), (_, index) => {
    const getDuration = () => {
      if (rangeType !== RangeType.YEAR) return { seconds: dateRangeType.intervalSeconds * index };
      if (index === 1 && from.getFullYear() !== to.getFullYear())
        return { days: differenceInCalendarDays(endOfMonth(dateFrom), from) + 1 };
      return { months: index };
    };
    const timestamp = add(from, getDuration()).getTime();
    return { date: getDate(timestamp, rangeType), timestamp };
  });
};

const addPlaceholderMeasurements = (
  measurements: IChartMeasurement[],
  rangeType: RangeType,
  dateFrom: Date,
  dateTo: Date,
): IChartMeasurement[] =>
  getDates(rangeType, dateFrom, dateTo).map((date, index) => {
    const tooltipText = getTooltipText(date.timestamp, rangeType, dateFrom, dateTo);

    const measurement = measurements.find((m) => m.date === date.date);
    if (measurement) {
      return {
        ...measurement,
        _id: index,
      };
    }

    return {
      _id: index,
      date: date.date,
      tooltipText,
    };
  });

const parseMeasurements = (
  measurements: IPhysiologicalMeasurements,
  rangeType: RangeType,
  dateFrom: Date,
  dateTo: Date,
): IChartMeasurement[] =>
  Object.values(measurements).map((measurement: IPhysiologicalMeasurement) => {
    const timestamp = toUTCDate(measurement._id).getTime();
    const date = getDate(timestamp, rangeType);
    const tooltipText = getTooltipText(timestamp, rangeType, dateFrom, dateTo);

    return {
      ...measurement,
      date,
      tooltipText,
    };
  });

export const parsePhysiologicalMeasurements = (
  measurements: IPhysiologicalMeasurements,
  rangeType: RangeType,
  dateFrom: Date,
  dateTo: Date,
): IChartMeasurement[] => {
  const chartMeasurements = parseMeasurements(measurements, rangeType, dateFrom, dateTo);
  const fulfilledChartMeasurements = addPlaceholderMeasurements(chartMeasurements, rangeType, dateFrom, dateTo);

  return fulfilledChartMeasurements;
};
